// ============================================================
// PRICING — what a batch really costs, and what to charge for it
// ============================================================
// Pure functions, no React and no storage, so the test suite can run them
// directly under plain node.
//
// The model follows how small food producers are advised to price:
//
//   1. Materials      what the recipe costs (ingredients + casings)
//   2. Yield          finished saleable weight after cook/smoke loss
//   3. Labor          your hours at an hourly rate
//   4. Overhead       monthly running costs, allocated per batch
//   5. Packaging      bags, vac pouches, labels
//   6. Price          set from a target margin, or entered directly
//   7. Set-aside      self-employment + income tax held back from profit
//
// Two costs are reported side by side throughout:
//   costPaid — your hours priced in, so profit is what the business earns
//              on top of paying you fairly
//   costFree — your hours excluded, so profit IS your take
// Neither is more correct; they answer different questions.

const LB_PER_G = 1 / 453.592;

// Self-employment tax: 15.3% applied to 92.35% of net earnings.
export const SE_TAX_EFFECTIVE = 0.153 * 0.9235;   // ≈ 0.14130

// Colorado Cottage Foods Act producer cap. Meat and refrigerated foods join the
// CCFA on 2027-01-01 under HB26-1033 ("Tamale Act"); the cap is inflation
// indexed, so treat this as the starting figure, not a permanent one.
export const CCFA_PRODUCER_CAP = 150000;
export const CCFA_MEAT_START   = '2027-01-01';

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

export const defaultPricingSettings = () => ({
  // Yield
  yieldLossPct:     25,     // cook/smoke loss, % of stuffable weight
                          // 20-30% is typical for smoked sausage; 25% is the
                          // working figure. Seed only — once the field is
                          // edited, sc_pricing holds whatever you typed.
  // Labor
  laborHours:       3,      // hours for one batch, start to packaged
  laborRate:        25,     // $/hour you pay yourself
  // Overhead — monthly running costs, spread across the month's batches
  monthlyOverhead:  0,      // propane, wood, electricity, insurance, fees
  batchesPerMonth:  4,
  registrationFee:  0,      // annual CDPHE registration, spread over a year
  // Packaging
  packageWeightLb:  1,      // saleable weight per package
  packageCost:      0,      // bag + label + vac pouch, per package
  // Price
  targetMarginPct:  55,     // gross margin on the selling price
  priceMode:        'margin',  // 'margin' = derive price | 'price' = set it
  manualPricePerLb: 0,
  // Tax
  taxSetAsidePct:   25,     // income tax held back, on top of SE tax
  applySeTax:       true,
  // Cap tracking
  salesToDate:      0,
});

// Load/merge saved settings so a stored copy from an older build still works.
export function normalizePricing(saved) {
  const base = defaultPricingSettings();
  if (!saved || typeof saved !== 'object') return base;
  const out = { ...base };
  Object.keys(base).forEach(k => {
    if (saved[k] === undefined || saved[k] === null) return;
    out[k] = typeof base[k] === 'number' ? num(saved[k], base[k]) : saved[k];
  });
  return out;
}

/**
 * Price one batch.
 *
 * @param materialCost  $ of ingredients + casings for the batch
 * @param stuffableG    grams of stuffable mix (what goes into casings)
 * @param s             pricing settings (see defaultPricingSettings)
 */
export function calcPricing(materialCost, stuffableG, settings) {
  const s = normalizePricing(settings);

  const materials = Math.max(0, num(materialCost));
  const stuffLb   = Math.max(0, num(stuffableG)) * LB_PER_G;

  // ── Yield: what you can actually sell ────────────────────────────────────
  const lossPct     = Math.min(99, Math.max(0, s.yieldLossPct));
  const finishedLb  = stuffLb * (1 - lossPct / 100);

  // ── Labor ────────────────────────────────────────────────────────────────
  const laborCost = Math.max(0, s.laborHours) * Math.max(0, s.laborRate);

  // ── Overhead per batch ───────────────────────────────────────────────────
  const batches     = Math.max(1, s.batchesPerMonth);
  const monthlyReg  = Math.max(0, s.registrationFee) / 12;
  const overhead    = (Math.max(0, s.monthlyOverhead) + monthlyReg) / batches;

  // ── Packaging ────────────────────────────────────────────────────────────
  const pkgLb    = Math.max(0.01, s.packageWeightLb);
  const packages = finishedLb > 0 ? Math.ceil(finishedLb / pkgLb) : 0;
  const pkgCost  = packages * Math.max(0, s.packageCost);

  // ── Two cost bases: your time paid, and your time free ───────────────────
  const costFree = materials + overhead + pkgCost;          // labor excluded
  const costPaid = costFree + laborCost;                    // labor included

  const perLb = (total) => finishedLb > 0 ? total / finishedLb : 0;
  const costPaidPerLb = perLb(costPaid);
  const costFreePerLb = perLb(costFree);

  // ── Price ────────────────────────────────────────────────────────────────
  // Margin is on the selling price (the food-industry convention), so
  //   price = cost / (1 - margin).
  // A 100% margin is unreachable, so the input is capped below it.
  const margin = Math.min(95, Math.max(0, s.targetMarginPct)) / 100;
  const suggestedPricePerLb = costPaidPerLb > 0
    ? costPaidPerLb / (1 - margin)
    : 0;

  const pricePerLb = s.priceMode === 'price'
    ? Math.max(0, s.manualPricePerLb)
    : suggestedPricePerLb;

  const pricePerPackage = pricePerLb * pkgLb;
  const revenue         = pricePerLb * finishedLb;

  // ── Profit, both ways ────────────────────────────────────────────────────
  const profitPaid = revenue - costPaid;   // business profit after paying you
  const profitFree = revenue - costFree;   // your total take for the batch

  const marginPaidPct = revenue > 0 ? (profitPaid / revenue) * 100 : 0;
  const marginFreePct = revenue > 0 ? (profitFree / revenue) * 100 : 0;

  // ── Tax set-aside ────────────────────────────────────────────────────────
  // Taxable earnings are what lands in your pocket: profit with your labor
  // counted as income, i.e. the labor-free figure.
  const taxable  = Math.max(0, profitFree);
  const seTax    = s.applySeTax ? taxable * SE_TAX_EFFECTIVE : 0;
  const incomeTax = taxable * Math.max(0, Math.min(100, s.taxSetAsidePct)) / 100;
  const totalSetAside = seTax + incomeTax;
  const takeHome      = profitFree - totalSetAside;

  // ── Break-even ───────────────────────────────────────────────────────────
  const breakEvenPaidPerLb = costPaidPerLb;
  const breakEvenFreePerLb = costFreePerLb;

  // ── CCFA cap ─────────────────────────────────────────────────────────────
  const salesToDate  = Math.max(0, s.salesToDate);
  const capRemaining = Math.max(0, CCFA_PRODUCER_CAP - salesToDate);
  const capUsedPct   = (salesToDate / CCFA_PRODUCER_CAP) * 100;
  const batchesToCap = revenue > 0 ? Math.floor(capRemaining / revenue) : 0;

  return {
    // Inputs echoed back
    stuffLb, finishedLb, lossPct, packages,
    // Cost lines
    materials, laborCost, overhead, pkgCost,
    costPaid, costFree,
    costPaidPerLb, costFreePerLb,
    // Price
    suggestedPricePerLb, pricePerLb, pricePerPackage, revenue,
    // Profit
    profitPaid, profitFree, marginPaidPct, marginFreePct,
    // Tax
    seTax, incomeTax, totalSetAside, takeHome,
    // Break-even
    breakEvenPaidPerLb, breakEvenFreePerLb,
    // Cap
    salesToDate, capRemaining, capUsedPct, batchesToCap,
  };
}
